<template>
  <span
    ref="el"
    class="tooltip-wrapper el-text--ellipsis v-tooltip-wrapper"
    data-testid="wrapper"
    @mouseenter="onMouseEnter"
  >
    <el-tooltip
      :disabled="!displayTooltip"
      :content="tooltipText"
      data-testid="tooltip"
      :enterable="enterable"
      placement="top"
      :max-width="tooltipWidth"
    >
      <span
        ref="inner"
        class="tooltip-wrapper-inner"
      >
        <slot />
      </span>
    </el-tooltip>
  </span>
</template>

<script lang="ts">
  import { ref, defineComponent } from 'vue';

  export default defineComponent({
    name: 'TooltipWrapper',
    props: {
      enterable: {
        type: Boolean,
        default: true
      },
      tooltipText: {
        type: [String, Number]
      },
      tooltipWidth: {
        type: Number
      },
      disabled: Boolean
    },
    setup(props) {
      const displayTooltip = ref(false);
      const inner = ref<HTMLElement | null>(null);

      // Measured on every hover rather than once: the element that gets clipped can
      // change width after mount (column resize, window resize, content update), so a
      // single cached verdict leaves truncated text without a tooltip.
      //
      // This was `@mouseenter.once` from VSTOR-30717 (table performance). Two layout
      // reads cost ~0.5us here, and the cost does not grow with the table: mouseenter
      // only fires for the cell under the cursor, so `.once` only saved repeat hovers
      // of the same cell. The case it really guarded — mouseenter storms while a long
      // table scrolls under a stationary cursor — is handled at the source by
      // `.is-scrolling .el-table__row { pointer-events: none }` in CommonTable.vue.
      function onMouseEnter() {
        if (!props.tooltipText || props.disabled) {
          displayTooltip.value = false;
          return;
        }

        const clientWidth = inner.value?.clientWidth ?? 0;
        const scrollWidth = inner.value?.scrollWidth ?? 0;

        displayTooltip.value = clientWidth < scrollWidth;
      }

      return {
        inner,
        displayTooltip,
        onMouseEnter
      };
    }
  });
</script>

<style lang="scss">
  .tooltip-wrapper {
    display: inline-block;
    vertical-align: middle;
  }

  .tooltip-wrapper-inner {
    overflow-x: hidden;
    text-overflow: ellipsis;
    display: block;
  }
</style>
